import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { STANDARD_LABEL, pct } from '../../lib/feasibilityLabels';
import { prettyUse } from '../../lib/zoningLabels';
import type { PlanningQuestion, ZoneNoticeView, ZoneStandardView } from '../../types/feasibility';

/**
 * FZ4 - a city's size limits, notices and open questions on the Zoning Rules screen (spec section 10).
 * ⛔ A held limit arrives from the server as its question only; this component could not show the value if it tried.
 */

const FRACTIONS = new Set(['lot_coverage_max', 'landscape_min']);
const FEET = new Set(['height_max_ft', 'setback_front_ft', 'setback_side_ft', 'setback_rear_ft']);
const SQUARE_FEET = new Set(['min_lot_sf', 'warehouse_large_threshold_sf']);
/** The 141st's look: 43 limits arrived in database order. A zone's limits read together, always in this order. */
const STANDARD_ORDER = [
  'far_max', 'lot_coverage_max', 'height_max_ft', 'stories_max', 'min_lot_sf', 'setback_front_ft', 'setback_side_ft',
  'setback_rear_ft', 'parking_per_1000_sf', 'landscape_min', 'warehouse_large_threshold_sf',
];
const rankOf = (standard: string) => {
  const i = STANDARD_ORDER.indexOf(standard);
  return i === -1 ? STANDARD_ORDER.length : i;
};
export const sortStandards = (rows: ZoneStandardView[]): ZoneStandardView[] =>
  [...rows].sort((a, b) => a.code.localeCompare(b.code) || rankOf(a.standard) - rankOf(b.standard) || a.applies_to.localeCompare(b.applies_to));

/** The server sends keys ("retail_store", "far_max (retail)"); the sheet goes to the City, so it says them in words. */
const subjectLabel = (q: PlanningQuestion): string => {
  if (q.kind === 'rule') return prettyUse(q.subject);
  if (q.kind === 'standard') {
    const m = /^([a-z0-9_]+) \(([a-z]+)\)$/.exec(q.subject);
    if (!m) return q.subject;
    return `${STANDARD_LABEL[m[1]] ?? m[1]}${m[2] !== 'all' ? ` (${m[2]})` : ''}`;
  }
  return q.subject.replace(/_/g, ' ');
};
const questionLine = (q: PlanningQuestion) => `${q.code ?? 'All zones'} - ${subjectLabel(q)}: ${q.question ?? q.status_note ?? 'needs an answer'}`;

export const planningSheet = (city: string, questions: PlanningQuestion[]): string =>
  [`Questions for ${city} Planning`, '', ...questions.map((q, i) => `${i + 1}. ${questionLine(q)}`)].join('\n');

const valueOf = (s: ZoneStandardView): string => {
  if (s.review_status !== 'approved') return '';
  if (!s.stated) return 'the code sets no limit';
  if (s.value === null || s.value === undefined) return '-';
  if (FRACTIONS.has(s.standard)) return pct(s.value);
  if (FEET.has(s.standard)) return `${s.value.toLocaleString('en-US')} ft`;
  if (SQUARE_FEET.has(s.standard)) return `${s.value.toLocaleString('en-US')} sq ft`;
  return s.value.toLocaleString('en-US');
};

export default function ZoneResearchPanel({ city }: { city: string }) {
  const [standards, setStandards] = useState<ZoneStandardView[]>([]);
  const [notices, setNotices] = useState<ZoneNoticeView[]>([]);
  const [questions, setQuestions] = useState<PlanningQuestion[]>([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    let live = true;
    setError('');
    api.getZoneResearch(city)
      .then((r) => { if (live) { setStandards(r.standards); setNotices(r.notices); setQuestions(r.questions); } })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : 'Could not read the research for this city'); });
    return () => { live = false; };
  }, [city]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(planningSheet(city, questions));
      setCopied('Copied the Planning sheet.');
    } catch {
      setCopied('Could not reach the clipboard - select the questions below and copy them.');
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label={`Size limits and questions for ${city}`}>
      <h2 className="font-semibold text-gray-900">{`Size limits, notices and open questions - ${city}`}</h2>
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-2 text-red-800">{error}</p>}
      {standards.length === 0 && !error && <p className="mt-2 text-gray-600">No size limits researched for this city yet.</p>}
      {standards.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full">
            <thead className="text-left text-xs uppercase text-gray-500"><tr><th className="p-1">Zone</th><th className="p-1">Limit</th><th className="p-1">Value</th><th className="p-1">Section</th><th className="p-1">Read</th></tr></thead>
            <tbody>
              {sortStandards(standards).map((s) => (
                <tr key={s.id} className="border-t align-top">
                  <td className="p-1">{s.code}</td>
                  <td className="p-1">{`${STANDARD_LABEL[s.standard] ?? s.standard}${s.applies_to !== 'all' ? ` (${s.applies_to})` : ''}`}</td>
                  <td className="p-1">{s.review_status === 'approved' ? valueOf(s) : <span className="text-amber-800">{`Held: ${s.question ?? s.status_note ?? 'a question is open'}`}</span>}</td>
                  <td className="p-1 text-xs text-gray-600">{s.code_section ?? ''}</td>
                  <td className="p-1 text-xs text-gray-600">{s.code_read_date ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {notices.length > 0 && (
        <ul className="mt-3 list-disc pl-5">
          {notices.map((n) => <li key={n.id}>{`${n.kind.replace('_', ' ')}: ${n.summary}${n.expires_on ? ` (until ${n.expires_on})` : ''}${n.review_status !== 'approved' ? ' - held' : ''}`}</li>)}
        </ul>
      )}
      <div className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-gray-900">{`Questions for ${city} Planning (${questions.length})`}</h3>
          {questions.length > 0 && <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => void copy()}>Copy the sheet</button>}
          {copied && <span role="status" className="text-xs text-gray-600">{copied}</span>}
        </div>
        <ol className="mt-1 list-decimal pl-5">
          {questions.map((q, i) => <li key={`${q.kind}-${q.code}-${q.subject}-${i}`}>{questionLine(q)}</li>)}
        </ol>
      </div>
    </section>
  );
}
